import { OPCOES_FILTRO_DATA } from '@constants/Text'
import {
  CLASSE_ANBIMA_COLUMN_KEY,
  WALLET_ASSET_COLUMN_CONFIG,
} from '@modules/consolidator/constants/wallet-asset-columns'
import { WALLET_MODO_CONFIGS } from '@modules/consolidator/utils/wallet-modo-configs/wallet-modo-configs'

/**
 * Constantes da carteira consolidada por GRUPO FAMILIAR.
 *
 * ★ Nada aqui calcula estatística. Rentabilidade, volatilidade, Sharpe e
 * rentabilidade relativa chegam prontos do backend, que os calcula sobre a
 * união dos ativos dos membros. Este arquivo só descreve COMO exibir.
 */

/** Nomes de rota — usados no router, no menu do consolidador e nas sub-abas. */
export const GROUP_WALLET_ROUTE = {
  SHELL: 'carteira.grupo-familiar',
  CARTEIRA_ATUAL: 'carteira.grupo-familiar.carteira-atual',
  EVOLUCAO: 'carteira.grupo-familiar.evolucao',
  ANALISE: 'carteira.grupo-familiar.analise',
  RISCO: 'carteira.grupo-familiar.risco',
}

export const GROUP_QUERY_KEY = 'id_grupo_familiar'

export const GROUP_EVOLUTION_DATA_BLOCKS = [
  'PL',
  'statistics',
  'posicao_completa',
  'evolucao_patrimonial',
]

/**
 * Sub-abas visíveis. `Análise de Risco` fica de fora enquanto o backend não tem
 * rota de risk-analysis com escopo de grupo — as cinco existentes são por
 * cliente e correlação de grupo não sai da média das dos membros. Voltar a
 * exibi-la é reinserir a linha aqui e devolver o `component` à rota.
 */
export const GROUP_WALLET_SUB_ROUTES = [
  { name: GROUP_WALLET_ROUTE.CARTEIRA_ATUAL, title: 'Carteira Atual' },
  { name: GROUP_WALLET_ROUTE.EVOLUCAO, title: 'Evolução Patrimonial' },
  { name: GROUP_WALLET_ROUTE.ANALISE, title: 'Análise Portfólio' },
]

/** Como cada célula da tabela "Por Carteira" é formatada. */
export const GROUP_WALLET_CELL_TYPE = {
  MEMBER: 'member',
  CURRENCY: 'currency',
  PERCENT: 'percent',
  RELATIVE: 'relative',
  DECIMAL: 'decimal',
  DATE: 'date',
}

export const GROUP_WALLET_COLUMNS = [
  {
    key: 'membro',
    label: 'Conta/Cliente',
    align: 'left',
    cellType: GROUP_WALLET_CELL_TYPE.MEMBER,
  },
  {
    key: 'pl',
    label: 'PL',
    align: 'right',
    cellType: GROUP_WALLET_CELL_TYPE.CURRENCY,
  },
  {
    key: 'rentabilidade',
    label: 'Rent.',
    align: 'right',
    cellType: GROUP_WALLET_CELL_TYPE.PERCENT,
  },
  {
    key: 'volatilidade',
    label: 'Vol.',
    align: 'right',
    cellType: GROUP_WALLET_CELL_TYPE.PERCENT,
  },
  {
    key: 'rentRelativa',
    label: 'Rent. Relativa',
    align: 'right',
    cellType: GROUP_WALLET_CELL_TYPE.RELATIVE,
  },
  {
    key: 'rent12m',
    label: 'Rent. 12M',
    align: 'right',
    cellType: GROUP_WALLET_CELL_TYPE.PERCENT,
  },
  {
    key: 'inicio',
    label: 'Início',
    align: 'right',
    cellType: GROUP_WALLET_CELL_TYPE.DATE,
  },
]

/** Mesma tabela, outro recorte de colunas — sub-aba Análise de Risco (AC-4.6). */
export const GROUP_RISK_COLUMNS = [
  {
    key: 'membro',
    label: 'Conta/Cliente',
    align: 'left',
    cellType: GROUP_WALLET_CELL_TYPE.MEMBER,
  },
  {
    key: 'volatilidade',
    label: 'Vol.',
    align: 'right',
    cellType: GROUP_WALLET_CELL_TYPE.PERCENT,
  },
  {
    key: 'sharpe',
    label: 'Sharpe',
    align: 'right',
    cellType: GROUP_WALLET_CELL_TYPE.DECIMAL,
  },
  {
    key: 'rentRelativa',
    label: 'Rent. Relativa',
    align: 'right',
    cellType: GROUP_WALLET_CELL_TYPE.RELATIVE,
  },
]

/** Estados possíveis da tela — discriminados pelo par (status, shape do value). */
export const GROUP_WALLET_UI_STATE = {
  EMPTY_NO_GROUPS: 'empty-no-groups',
  EMPTY_NO_GROUP: 'empty-no-group',
  LOADING: 'loading',
  BLOCKED: 'blocked',
  NO_MEMBERS: 'no-members',
  NO_COTIZED: 'no-cotized',
  ASSET_CAP: 'asset-cap',
  BLOCKED_MEMBER: 'blocked-member',
  ERROR: 'error',
  READY: 'ready',
}

export const GROUP_WARNING_SEVERITY = {
  INFO: 'info',
  WARNING: 'warning',
}

/**
 * Severidade por tipo de aviso. Benchmark divergente é informativo (o número
 * está certo, só difere da tela individual); os demais afetam a confiança no
 * número e sobem para atenção.
 */
export const GROUP_WARNING_SEVERITY_BY_KEY = {
  membro_sem_ativos: GROUP_WARNING_SEVERITY.WARNING,
  conta_conjunta: GROUP_WARNING_SEVERITY.WARNING,
  frescor_membro: GROUP_WARNING_SEVERITY.WARNING,
  benchmark_divergente: GROUP_WARNING_SEVERITY.INFO,
  modo_degradado: GROUP_WARNING_SEVERITY.WARNING,
}

/**
 * Fallback de texto quando o backend não manda `mensagem`. Existe para que um
 * aviso nunca desapareça da tela por falha de contrato — e continua nomeando o
 * membro, como exige a AC-8.7.
 */
export const GROUP_WARNING_FALLBACK_BY_KEY = {
  membro_sem_ativos:
    'não possui ativos cotizados e não foi incluído na consolidação.',
  conta_conjunta:
    'compartilham uma conta; o patrimônio dela pode estar contado duas vezes.',
  frescor_membro: 'está com dados desatualizados.',
  benchmark_divergente:
    'usa outro benchmark na visão individual, então a rentabilidade relativa será diferente naquela tela.',
  modo_degradado:
    'está em modo de cálculo simplificado; a rentabilidade do grupo pode ter pequena imprecisão.',
}

/** Texto permanente sob a tabela "Por Carteira" — não é tooltip (MV1). */
export const GROUP_TOTAL_NOTE =
  'Rentabilidade, volatilidade e relativa do grupo são recalculadas sobre a carteira somada. Não são a soma nem a média das linhas acima — apenas o PL é somável.'

export const GROUP_TOTAL_EXPLAINER_LINK = 'Como o total do grupo é calculado'

export const GROUP_TOTAL_ROW_LABEL = 'Total do grupo'
export const GROUP_TOTAL_ROW_LABEL_FILTERED = 'Total (filtrado)'

/**
 * Exemplo do modal explicativo. É ESTÁTICO de propósito: derivá-lo do grupo
 * real seria um segundo cálculo no frontend, que a AC-2.3 proíbe. Os números
 * são os do `spec.md §6.5` — material que o consultor usa para explicar à
 * família.
 */
export const GROUP_TOTAL_EXPLAINER_EXAMPLE = {
  colunas: ['', 'Dia 1', 'Dia 2', 'No período'],
  linhas: [
    {
      label: 'Ricardo (CDB)',
      dia1: '0,00%',
      dia2: '0,00%',
      periodo: '0,00%',
      destaque: false,
    },
    {
      label: 'Helena (Ações)',
      dia1: '+10,00%',
      dia2: '-10,00%',
      periodo: '-1,00%',
      destaque: false,
    },
    {
      label: 'Grupo (somado)',
      dia1: '+0,909%',
      dia2: '-5,000%',
      periodo: '-4,14%',
      destaque: true,
    },
  ],
}

/** Chave base do donut "Distribuição por Conta" (contrato do componente). */
export const GROUP_ACCOUNT_DISTRIBUTION_BASE_KEY = 'conta'

export const GROUP_ASSET_ROW_ACTIONS = Object.freeze(['detalhes', 'editar'])

/** Valor exibido quando o backend não tem o dado. */
export const GROUP_EMPTY_CELL = '-'

// ---------------------------------------------------------------------------
// Barra de filtros
// ---------------------------------------------------------------------------

/** Chave do filtro de conta — é o primeiro seletor da barra (AC-3.4). */
export const GROUP_FILTER_ACCOUNT_KEY = 'id_conta'
/** Chave do filtro de membro do grupo. */
export const GROUP_FILTER_MEMBER_KEY = 'id_cliente'
export const GROUP_FILTER_GROUP_BY_KEY = 'group_by'
export const GROUP_FILTER_INVESTMENT_TYPE_KEY = 'investment_type'
export const GROUP_FILTER_DATE_KEY = 'filtroData'

/**
 * Dimensões de agrupamento. Derivadas de `WALLET_MODO_CONFIGS` para que a lista
 * do grupo não divirja da lista da visão por cliente com o tempo; a entrada do
 * membro vem à parte porque aqui ela abre a lista (é o que alimenta a seção
 * "Por Carteira") e usa o texto do grupo.
 *
 * ★ O `filter` NÃO é decorativo: `WALLET_MODO_CONFIGS` também descreve o membro
 * (rotulado "Membro", para a visão por cliente), então sem ele o select do grupo
 * oferece `id_cliente` DUAS vezes — o `<v-select>` é single e `itemValue` é
 * `value`, então as duas linhas viram a mesma opção com nomes diferentes.
 */
export const GROUP_BY_OPTIONS = [
  { value: GROUP_FILTER_MEMBER_KEY, text: 'Membro do grupo' },
  ...WALLET_MODO_CONFIGS.filter(
    (config) => config.filterParam !== GROUP_FILTER_MEMBER_KEY
  ).map((config) => ({
    value: config.filterParam,
    text: config.label,
  })),
]

export const GROUP_INVESTMENT_TYPE_OPTIONS = [
  { value: '', text: 'Todos' },
  { value: 'onshore', text: 'Onshore' },
  { value: 'offshore', text: 'Offshore' },
]

/**
 * Períodos disponíveis. "Específica" fica de fora: o intervalo livre depende do
 * modal de data da visão por cliente, que ainda não atende o contexto de grupo.
 */
export const GROUP_DATE_OPTIONS = Object.values(OPCOES_FILTRO_DATA)
  .filter((option) => option !== OPCOES_FILTRO_DATA.ESPECIFICA)
  .map((option) => ({ value: option, text: option }))

/**
 * Ordem dos filtros de dimensão, seguindo o protótipo.
 *
 * ★ Cópia deliberada, não derivação. O CONJUNTO é o de `WALLET_MODO_CONFIGS`
 * menos as dimensões de id (Conta e Membro, que têm seletor próprio na barra),
 * mas a ORDEM é a do protótipo e não a de declaração do compartilhado —
 * derivar mudaria a ordem do dropdown, que é comportamento visível. O que a
 * derivação daria de graça está travado pelo lock
 * `should offer every shared dimension that is not an id`: dimensão nova,
 * renomeada ou removida no compartilhado deixa esta lista vermelha.
 */
export const GROUP_DIMENSION_FILTER_ORDER = [
  'subclasse',
  'corretora',
  'nome_portfolio',
  'classe',
  'tipo',
  'subtipo',
  'setor',
  'emissor',
  'tags',
]

/**
 * Catálogo de colunas da tabela de ativos DO GRUPO: o mesmo da carteira do
 * cliente, mais a coluna `Membro`, logo depois de `Ativo`.
 *
 * ★ Derivado, nunca uma cópia. O catálogo do cliente é artefato COMPARTILHADO —
 * editá-lo pra caber o grupo mudaria a tela do cliente junto. Aqui o grupo monta
 * o SEU e injeta via prop `columnsConfig`; quem não passa nada segue com o
 * default e não muda em nada.
 */
export const GROUP_ASSET_COLUMN_KEY_MEMBER = 'membro'

/**
 * @param {Object} ativo linha da carteira
 * @param {Array} membros lista `membros` do payload de consolidacao
 * @returns {String} nome do membro, ou `-`
 */
export const resolveNomeDoMembro = (ativo, membros = []) => {
  if (!ativo) return '-'
  const direto = ativo.membro || ativo.nome_cliente
  if (direto) return direto
  const id = ativo.id_cliente
  if (id === null || id === undefined) return '-'
  const membro = (membros || []).find(
    (m) => m && String(m.id_cliente_app) === String(id)
  )
  return (membro && (membro.nome || membro.email || membro.login)) || '-'
}

/**
 * Colunas ligadas por default na carteira do grupo. Toda chave, menos a do
 * próprio membro, tem que existir em `WALLET_ASSET_COLUMN_CONFIG`: a lista é
 * aplicada por igualdade de string em `buildGroupAssetColumnConfig`, então uma
 * chave renomeada no catálogo compartilhado não dá erro — vira `defaultOn:
 * false` e a coluna SOME calada da tabela. As que já são constantes nomeadas
 * entram pela constante; as demais estão travadas pelo lock
 * `should default on only columns the shared catalog actually has`.
 */
export const GROUP_ASSET_DEFAULT_COLUMN_KEYS = [
  'ativo',
  GROUP_ASSET_COLUMN_KEY_MEMBER,
  'instituicao',
  'saldo_valor',
  'saldo_pct',
  'rent_valor',
  CLASSE_ANBIMA_COLUMN_KEY,
]

/**
 * Monta o catálogo de colunas da tabela de ativos do grupo.
 *
 * ★ NÃO recebe a lista de membros. O titular já chega carimbado no ativo por
 * `enrichWalletWithMembers`, e `resolveNomeDoMembro` lê esse carimbo antes de
 * qualquer busca — é o contrato das DUAS telas que montam a tabela do grupo
 * (`minha-carteira.vue` e `views/grupo-familiar/view-group-carteira-atual.vue`),
 * que passam a carteira enriquecida. Um 2º argumento aqui acoplaria o catálogo
 * ao ciclo de vida do payload: `carteira-ativos.vue` reseta a seleção de colunas
 * do usuário quando o array do catálogo troca de INSTÂNCIA, e a lista de membros
 * é reconstruída a cada recarga.
 *
 * @param {Array} walletConfig catálogo de colunas da carteira do cliente
 * @returns {Array} o mesmo catálogo com a coluna `Membro` depois de `Ativo`
 */
export const buildGroupAssetColumnConfig = (walletConfig) => {
  const config = Array.isArray(walletConfig) ? walletConfig : []
  // `cellType: 'text'` + `value` e o contrato GENERICO da tabela: cabecalho e
  // celula saem sozinhos, sem precisar de uma branch `v-else-if` por chave nos
  // dois componentes. Com `custom` a coluna existia no catalogo e nao renderizava
  // em lugar nenhum — some calada, que e o pior sintoma.
  const membro = {
    key: GROUP_ASSET_COLUMN_KEY_MEMBER,
    label: 'Membro',
    defaultOn: true,
    cellType: 'text',
    align: 'left',
    value: (ativo) => resolveNomeDoMembro(ativo),
  }
  if (config.length === 0) return [membro]
  const comMembro = [config[0], membro].concat(config.slice(1))
  // A carteira do grupo NAO tem seletor de colunas (e leitura), entao o conjunto
  // exibido e fixo — e ele reproduz o da tabela que existia antes do reuso:
  // Ativo · Membro · Instituicao · Saldo Bruto · % PL · Rentabilidade · Classe ANBIMA.
  // As demais ficam no catalogo (o cabecalho sabe agrupa-las) mas nascem
  // desligadas, em vez de sumirem da estrutura.
  return comMembro.map((column) => ({
    ...column,
    defaultOn: GROUP_ASSET_DEFAULT_COLUMN_KEYS.includes(column.key),
  }))
}

export const GROUP_ASSET_COLUMN_CONFIG = buildGroupAssetColumnConfig(
  WALLET_ASSET_COLUMN_CONFIG
)
